import type { ActionEnum, TreeActionableContext } from 'venn-api';
import type { DropMenuItem } from 'venn-ui-kit';
import { flattenContextTree } from 'venn-utils';

const GLOBAL_LABEL = 'Global';

export const getContextMenuItems = (
  contextTree?: TreeActionableContext[],
  currentContext?: string,
  requiredAction?: ActionEnum,
): DropMenuItem<string>[] => {
  const availableContextItems = getAvailableContextItems(contextTree ?? []);

  if (requiredAction) {
    const hasRequiredPermissions = new Set<string>();
    (contextTree ?? []).forEach((context) =>
      currentContext
        ? findHasRequiredPermissionsFromCurrentContext(context, requiredAction, hasRequiredPermissions, currentContext)
        : findHasRequiredPermissions(context, requiredAction, hasRequiredPermissions),
    );
    return availableContextItems.filter((item) => hasRequiredPermissions.has(item.value) || item.label === '');
  }
  return availableContextItems;
};

const getAvailableContextItems = (contextTree: TreeActionableContext[]) => {
  return (contextTree ?? [])
    .flatMap((tree) => flattenContextTree(tree, 0))
    .map((item) => ({
      value: item.context.id,
      label: item.context.name,
      level: item.level,
    }));
};

export const getContextNameById = (contextTree: TreeActionableContext[], contextId: string): string | undefined => {
  const availableContextItems = getAvailableContextItems(contextTree);
  const contextItem = availableContextItems.find((item) => item.value === contextId);
  return contextItem?.label;
};

const findHasRequiredPermissions = (
  root: TreeActionableContext,
  requiredAction: ActionEnum,
  hasRequiredPermissions: Set<string>,
) => {
  if (root.value.actionEnums.includes(requiredAction)) {
    hasRequiredPermissions.add(root.value.context.id);
  }

  root.children.forEach((child) => findHasRequiredPermissions(child, requiredAction, hasRequiredPermissions));
};

const findHasRequiredPermissionsFromCurrentContext = (
  root: TreeActionableContext,
  requiredAction: ActionEnum,
  hasRequiredPermissions: Set<string>,
  currentContext: string,
): boolean => {
  let isRootOnPath = root.value.actionEnums.includes(requiredAction);

  if (currentContext !== root.value.context.id && GLOBAL_LABEL !== root.value.context.name) {
    isRootOnPath =
      isRootOnPath &&
      root.children
        .map((child) =>
          findHasRequiredPermissionsFromCurrentContext(child, requiredAction, hasRequiredPermissions, currentContext),
        )
        .reduce((a, b) => a || b, false);
  }

  if (isRootOnPath) {
    hasRequiredPermissions.add(root.value.context.id);
  }

  return isRootOnPath;
};
